// helpers/pricerise.js

/**
 * Safely parse a date string in "DD/MM/YYYY" format.
 * @param {string} str
 * @returns {Date | null}
 */
function parseDDMMYYYY(str) {
    if (typeof str !== "string") return null;
    const parts = str.split("/");
    if (parts.length !== 3) return null;
    const [dayS, monthS, yearS] = parts;
    const day = parseInt(dayS, 10);
    const month = parseInt(monthS, 10);
    const year = parseInt(yearS, 10);
    if (isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || month < 1 || month > 12) {
        return null;
    }
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
        return null;
    }
    d.setHours(0, 0, 0, 0);
    return d;
}

/**
 * Parse a date string or Date object.
 * @param {string|Date} raw
 * @returns {Date | null}
 */
function parseDate(raw) {
    if (!raw) return null;
    if (raw instanceof Date) {
        const d = new Date(raw);
        d.setHours(0, 0, 0, 0);
        return d;
    }
    if (typeof raw === "string") {
        // Try DD/MM/YYYY first
        const d1 = parseDDMMYYYY(raw);
        if (d1) return d1;
        // Fallback to ISO
        const tmp = new Date(raw);
        if (!isNaN(tmp.getTime())) {
            tmp.setHours(0, 0, 0, 0);
            return tmp;
        }
    }
    return null;
}

/**
 * Check if price rise feature is active.
 */
export function isPriceRiseActive(app) {
    const rawActive = app?.priceRise?.price_rise_active;
    return typeof rawActive === "string" && rawActive.trim().toLowerCase() === "true";
}

/**
 * Check if today is before the price rise start date.
 * We show warnings only while today < start date.
 * @param {object} app
 * @param {Date} [today=new Date()]
 * @returns {boolean}
 */
export function isBeforePriceRiseStart(app, today = new Date()) {
    const startRaw = app?.priceRise?.price_rise_start_date;
    const startDate = parseDate(startRaw);
    if (!startDate) return false;
    const todayMid = new Date(today);
    todayMid.setHours(0, 0, 0, 0);
    return todayMid < startDate;
}

/**
 * Check if delivery date is invalid (<14 days from today or missing).
 */
export function isDeliveryDateInvalid(job, today = new Date()) {
    const deliveryRaw = job?.QuoteParameters?.DeliveryDate;
    const deliveryDate = parseDate(deliveryRaw);
    const todayMid = new Date(today);
    todayMid.setHours(0, 0, 0, 0);
    const threshold = new Date(todayMid);
    threshold.setDate(threshold.getDate() + 14);
    return !deliveryDate || deliveryDate < threshold;
}

/**
 * Composite: active & before start date & invalid delivery.
 * Show warning until the price rise start date.
 * @param {object} app
 * @param {object} job
 * @param {Date} [today=new Date()]
 * @returns {boolean}
 */
export function shouldWarnPriceRiseDelivery(app, job, today = new Date()) {
    return isPriceRiseActive(app) && isBeforePriceRiseStart(app, today) && isDeliveryDateInvalid(job, today);
}
